import { AlertTriangle, Check, X, Minus } from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { cn } from '@/lib/utils';

/** `warning`: the leg answered but with a caveat (Azure Storage: listed, yet the data read was refused). */
export type LegStatus = 'pending' | 'ok' | 'error' | 'not-attempted' | 'skipped' | 'warning';

/**
 * One leg of a Test connection answer, where the backend reports legs: the SSH tunnel and the database (an SSH run),
 * or one per ENABLED Azure Storage service (spec §4.5, known only at runtime). Rendered in array order as
 * `data-testid="test-step-<id>"`.
 */
export type TestLeg = {
  id: string;
  label: string;
  status: LegStatus;
  detail: string;
  /** The service's own sentence behind a "Details" disclosure, when `detail` is Jam SQL's plain reading of it (C2 N19). */
  details?: string;
  /** A remedy link after the detail (Azure Storage: "Add share names"); rendered only when the caller handles it. */
  action?: { id: string; label: string };
};

type Props = { legs: TestLeg[]; onLegAction?: (actionId: string) => void };

/**
 * All status icons stay mounted and cross-fade (opacity + scale + blur) when a
 * leg transitions (pending → ok/error), instead of hard-swapping components.
 */
function statusIconClass(isActive: boolean, extra?: string) {
  return cn(
    'absolute inset-0 h-3.5 w-3.5 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
    isActive ? 'opacity-100 scale-100 blur-[0px]' : 'opacity-0 scale-[0.25] blur-[4px]',
    extra,
  );
}

function StatusIcon({ status }: { status: LegStatus }) {
  const isNeutral = status === 'not-attempted' || status === 'skipped';
  return (
    <span className="relative h-3.5 w-3.5 shrink-0">
      <Check className={statusIconClass(status === 'ok', 'text-green-600 dark:text-green-500')} />
      <X className={statusIconClass(status === 'error', 'text-destructive')} />
      <AlertTriangle className={statusIconClass(status === 'warning', 'text-warning')} />
      <Minus className={statusIconClass(isNeutral, 'text-muted-foreground')} />
      {/* The spinner is a `role="status"` "Loading" region of its own: hidden from assistive technology once it has faded
          out, or every finished row would still announce "Loading". */}
      <span className={statusIconClass(status === 'pending', 'flex items-center justify-center')} aria-hidden={status !== 'pending' || undefined}>
        <RandomDotmSpinner size={14} />
      </span>
    </span>
  );
}

/**
 * U35 (A+B merge): a quoted name ("e2e-s3-props") wraps as one piece, so a line never ends on its opening quote and
 * the name never splits at its hyphen — unless the name alone is wider than the row. Last UI/UX pass (F12): an opening
 * bracket before it and the punctuation after it travel with it (a line ended on a lone "(").
 */
function LegDetailText({ text }: { text: string }) {
  const parts = text.split(/([([]?"[^"\n]+"[)\],.;:]*)/u);
  return (
    <>
      {parts.map((part, index) => (index % 2 === 1
        ? <span key={index} className="inline-block max-w-full [overflow-wrap:anywhere]" data-testid="test-step-quoted">{part}</span>
        : part))}
    </>
  );
}

/**
 * The chip carries the leg's status (icon, and colour on the label — round B U2: never colour alone, and never red /
 * amber body text, which failed 4.5:1); the plain reading follows it in normal text. A skipped leg is "not verified",
 * not a failure: amber, never red (spec §4.5).
 */
const CHIP: Record<LegStatus, string> = {
  ok: 'border-border bg-background text-foreground',
  pending: 'border-border bg-background text-muted-foreground',
  error: 'border-destructive/40 bg-destructive/10 text-destructive',
  warning: 'border-warning/40 bg-warning/10 text-warning',
  skipped: 'border-warning/40 bg-warning/10 text-warning',
  'not-attempted': 'border-border bg-muted text-muted-foreground',
};

function LegRow({ leg, onAction }: { leg: TestLeg; onAction?: (actionId: string) => void }) {
  const testId = `test-step-${leg.id}`;
  const detail = leg.status === 'pending' && !leg.detail ? 'In progress…' : leg.detail;
  return (
    <div data-testid={testId} data-status={leg.status} className="flex items-start gap-2 py-0.5 text-sm">
      <span className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium', CHIP[leg.status])}>
        <StatusIcon status={leg.status} />
        {leg.label}
      </span>
      <span className="min-w-0 break-words pt-px text-foreground">
        <LegDetailText text={detail} />
        {leg.action && onAction && (
          <button
            type="button"
            className="ml-1.5 font-medium text-primary underline-offset-2 hover:underline"
            onClick={() => onAction(leg.action!.id)}
            data-testid={`${testId}-action`}
          >
            {leg.action.label}
          </button>
        )}
        {/* Informational, like "Error details": no analytics (round B decision 9). */}
        {leg.details && (
          <details className="mt-0.5 text-xs text-muted-foreground" data-testid={`${testId}-details`}>
            <summary className="w-fit cursor-pointer select-none hover:text-foreground">Details</summary>
            <span className="mt-0.5 block"><LegDetailText text={leg.details} /></span>
          </details>
        )}
      </span>
    </div>
  );
}

/**
 * The legs of one answer, each a status chip and its reading (Option C C2: one shape for SSH and Azure Storage). The
 * result strip around it is the live region (`role="status"`), so the rows are not live regions of their own.
 */
export function TestConnectionStepper({ legs, onLegAction }: Props) {
  return (
    <div data-testid="connection-test-stepper" className="flex flex-col gap-0.5">
      {legs.map((leg) => <LegRow key={leg.id} leg={leg} onAction={onLegAction} />)}
    </div>
  );
}
